import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import TabPageLayout, { DataCard } from "./TabPageLayout";
import { LayoutActionButton } from "./Buttons";
import StatusBadge from "./StatusBadge";

const REGION_INFO = {
  UAE: "AED · Asia/Dubai · WhatsApp-first comms · Eid & National Day holiday pricing",
  USA: "USD · US timezones · SMS-first comms · July 4th / Labor Day / Spring Break holiday pricing",
};

const buildSections = (region) => {
  const uae = region === "UAE";
  const currency = uae ? "AED" : "USD";
  const tz = uae ? "Asia/Dubai" : "their local US timezone (ET/CT/MT/PT)";
  const holidays = uae
    ? "Eid Al Fitr, Eid Al Adha, UAE National Day, NYE"
    : "Memorial Day, July 4th, Labor Day, Spring Break, NYE";
  const comms = uae
    ? "WhatsApp-first (preferred in the Gulf) + email; SMS as backup"
    : "SMS-first + email; 10-digit US mobile numbers (validated at checkout)";
  const reviews = uae
    ? "Google Business Profile and/or TripAdvisor link"
    : "Google Business Profile link (and Yelp/TripAdvisor if used)";
  const extraPay = uae
    ? "Multi-currency display for international tourists (AED base — customers can pay in USD/EUR/GBP)"
    : "Tap to Pay on iPhone/Android for on-dock balance collection and captain-side charges";

  return [
    { title: "1 · Company Basics", required: true, items: [
      "Legal name + trading/brand name",
      "Website URL (or none — we host their booking page)",
      "Primary contact: name, mobile" + (uae ? " (WhatsApp?)" : "") + ", email",
      "Operating locations: marina / beach / dock / pickup points",
      `Timezone (${tz}) + currency (${currency})`,
    ]},
    { title: "2 · Branding", required: true, items: [
      "Logo (PNG/SVG) + brand colors (hex) — or 'match our website'",
      `Review link (${reviews}) for automated post-trip review requests`,
      "Social links; where the booking widget lives (their site page or hosted page)",
    ]},
    { title: "3 · Website & Integration", required: false, items: [
      "Do they have a website? URL + who manages it (CMS/hosting access or their web person's contact)",
      "Want us to integrate the booking software into their site for them? Our engineers are happy to do it",
      "No website? We can build one for them / host their branded booking page",
    ]},
    { title: "4 · Roles & Team", required: true, items: [
      "EVERY team member: full name, mobile, email, role (Owner / Admin / Manager / Captain / Crew)",
      "Which roles can: see revenue, edit bookings, run trips, check in guests",
      `Who gets booking notifications and for which events — ${comms}`,
      "2FA on owner/admin logins? (recommended)",
    ]},
    { title: "5 · Assets", required: true, items: [
      "Each boat/jet ski/vehicle: name, type, capacity (max guests), photos",
      "Which roles/team members can operate it",
      "Home marina/dock + known maintenance or blocked dates",
    ]},
    { title: "6 · Services — the core", required: true, items: [
      "Name, customer-facing description, photos per service",
      `Duration options + price per duration (e.g. 2hr ${currency} 400 / 4hr ${currency} 700)`,
      "Party size min–max; per-group or per-person pricing",
      "Time slots / operating hours per day of week",
      `Seasonal & HOLIDAY pricing (${holidays} — dates + adjusted prices)`,
      "Deposit: none / % / fixed (balance due at check-in?)",
      "Add-ons (catering, photographer, fuel, transfers … + prices)",
      "Custom questions (e.g. children/infants count for life jackets, special occasion)",
      "Cancellation/refund + weather policy; which assets can run each service",
    ]},
    { title: "7 · Payments — Stripe Connect", required: true, items: [
      `They complete Stripe Connect onboarding via our link (~10 min): legal details, ID, ${currency} bank account`,
      "Confirm who owns the Stripe account login (email)",
      extraPay,
    ]},
    { title: "8 · Waivers", required: false, items: [
      "Their waiver/liability text (PDF/doc) — we digitize it",
      "Which services require signing; verification via email OTP and/or SMS OTP",
    ]},
    { title: "9 · Existing Data Migration (if switching systems)", required: false, items: [
      "Export of FUTURE bookings — CSV or JSON preferred, but ANY format works (spreadsheet, PDF, screenshots — we'll migrate it)",
      "Data per booking: customer name/phone/email, service, date/time, party size, paid/balance, source (Fareharbor/Boatsetter/GetMyBoat/etc.)",
      "Customer list (optional, for marketing); outstanding gift cards/credits to honor",
    ]},
    { title: "10 · Marketing & Tracking", required: false, items: [
      "Google Analytics 4 tag (G-XXXX)",
      "Google Ads conversion tag + label",
      "Meta (Facebook) Pixel ID",
      "Discount codes to create (code, %/amount, expiry, usage limits)",
      "Gift cards — capability exists, can be set up later if wanted",
    ]},
    { title: "11 · Other Ways We Can Help", required: false, items: [
      "SEO help for their website / booking page",
      "Feature requests or custom workflows they'd like",
      "Follow-ups and regular check-ins after go-live",
      "Live demos of features and functionality for their team",
      uae ? "WhatsApp notification channel preferences, Ramadan/summer schedule adjustments" : "SMS notification preferences, quiet hours, off-season schedule adjustments",
    ]},
  ];
};

const HOW_IT_WORKS = [
  { step: "1", title: "Copy & send", text: "Copy this checklist and send it to the operator (email or WhatsApp)." },
  { step: "2", title: "Collect", text: "The operator replies with answers + files (logo, waiver, booking exports). The rep chases anything missing." },
  { step: "3", title: "Hand off", text: "Forward the completed intake to BookingFlows — we do the full setup and go live in 2–3 business days." },
];

const OnboardingIntake = () => {
  const [region, setRegion] = useState("UAE");
  const [copied, setCopied] = useState(false);
  const sections = buildSections(region);

  const handleCopy = async () => {
    const text = [
      `${region} Company Onboarding Questionnaire — BookingFlows master intake doc`,
      `Region: ${region} (${REGION_INFO[region]})`,
      "Collect everything below from the company — BookingFlows handles the rest. Typical go-live: 2–3 business days after complete intake.",
      "",
      ...sections.map((s) =>
        `${s.title}${s.required ? " (required)" : " (optional)"}\n` +
        s.items.map((i) => `  - ${i}`).join("\n")
      ),
    ].join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${region} questionnaire copied to clipboard`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  return (
    <TabPageLayout
      title="Onboarding"
      subtitle="Rinse-and-repeat intake checklist for onboarding new companies — typical go-live is 2–3 business days after complete intake"
      actions={
        <LayoutActionButton icon={copied ? Check : Copy} onClick={handleCopy} data-testid="onboarding-copy-btn">
          {copied ? "Copied" : "Copy as text"}
        </LayoutActionButton>
      }
    >
      <div className="space-y-6" data-testid="onboarding-intake-page">
        {/* How it works */}
        <DataCard>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="onboarding-how-it-works">
            {HOW_IT_WORKS.map((s) => (
              <div key={s.step} className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold flex items-center justify-center flex-shrink-0">
                  {s.step}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{s.title}</p>
                  <p className="text-sm text-gray-500 mt-0.5">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </DataCard>

        {/* Step 1 — pick the region */}
        <DataCard title="Start here — where is the operator based?" data-testid="onboarding-region-card">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex bg-gray-100 rounded-lg p-1 w-fit">
              {["UAE", "USA"].map((r) => (
                <button
                  key={r}
                  onClick={() => setRegion(r)}
                  data-testid={`onboarding-region-${r.toLowerCase()}`}
                  className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
                    region === r ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-500" data-testid="onboarding-region-info">{REGION_INFO[region]}</p>
          </div>
        </DataCard>

        {/* Sections — one step at a time, full width */}
        <div className="space-y-4">
          {sections.map((s) => (
            <DataCard
              key={s.title}
              title={s.title}
              actions={<StatusBadge status={s.required ? "required" : "optional"} label={s.required ? "Required" : "Optional"} />}
              data-testid={`onboarding-section-${s.title.split(" ")[0]}`}
            >
              <ul className="space-y-2">
                {s.items.map((item, i) => (
                  <li key={i} className="text-sm text-gray-600 flex gap-2.5">
                    <span className="text-gray-300 flex-shrink-0 mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </DataCard>
          ))}
        </div>
      </div>
    </TabPageLayout>
  );
};

export default OnboardingIntake;
